import React, { useState } from 'react';
import mixpanel from 'mixpanel-browser';
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/20/solid';
import { useNavigate } from 'react-router-dom';
import useSWRMutation from 'swr/mutation';
import { Cluster, Button, DropdownItem, DropdownMenu, Modal, Loader } from '@nearst/ui';

import { disableChannel } from '@services/channels';
import { useShop } from '@services/ShopProvider';

import styles from './ChannelActions.module.scss';

const ChannelActions = ({ channelId, channelTitle }) => {
	const [showDialog, setShowDialog] = useState(false);
	const [loading, setLoading] = useState(false);
	const [updated, setUpdated] = useState(false);
	const [error, setError] = useState(false);
	const { shop } = useShop();
	const navigate = useNavigate();
	const { trigger } = useSWRMutation(['channel', shop.id, channelId], async () => disableChannel(channelId, shop.id));
	const close = () => {
		setShowDialog(false);
		if (updated) {
			navigate(`/channels/${shop.id}/details/${channelId}`);
		}
	};
	const open = () => {
		setShowDialog(true);
		setUpdated(false);
		setError(false);
	};

	const confirm = async () => {
		setLoading(true);
		setError(false);
		mixpanel.track(`${channelTitle} disabled`, { distinct_id: shop.id });
		try {
			await trigger(null, {
				optimisticData: (existing) => ({ ...existing, status: 'disabled' }),
				populateCache: (result, currentData) => ({ ...currentData, status: 'disabled' }),
				rollbackOnError: true,
				revalidate: false
			});
			setUpdated(true);
		} catch (e) {
			console.error(e);
			setError(true);
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			<DropdownMenu secondary mini placement="bottom-end" buttonText="Actions">
				<DropdownItem className={styles.warning} onClick={open}>
					Disable channel
				</DropdownItem>
			</DropdownMenu>
			<Modal className={styles.modal} isOpen={showDialog} onDismiss={close}>
				<div className={styles.modalTitleContainer}>
					<h2 className={styles.modalTitleText}>Disable {channelTitle}</h2>
					<button className={styles.modalExitButton} onClick={close}>
						<XMarkIcon height={18} />
					</button>
				</div>
				<div className={styles.modalContainer}>
					{updated ? (
						<>
							<p className={styles.success}>
								<CheckCircleIcon height={18} />
								We've disabled all updates to your menu. Please use the {channelTitle} dashboard to remove the link to
								NearSt.
							</p>
							<Cluster className={styles.modalActionButton}>
								<Button onClick={close}>Close</Button>
							</Cluster>
						</>
					) : (
						<>
							<p>
								This will disable all updates to your menu. You will also need to use the {channelTitle} dashboard to remove
								the link to NearSt.
							</p>
							<Cluster className={styles.modalActionButton}>
								<Button disabled={loading} onClick={confirm} warning>
									Disable {channelTitle}
								</Button>
								{loading && <Loader />}
								{error && <p>There was an error disabling {channelTitle}. Please try again or contact support.</p>}
							</Cluster>
						</>
					)}
				</div>
			</Modal>
		</>
	);
};

export default ChannelActions;
